import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Building2, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PlatformAdmin() {
  const [tenants, setTenants] = useState(null);
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get("/platform/tenants"), api.get("/platform/stats")]);
      setTenants(t.data.items);
      setStats(s.data);
    } catch {
      toast.error("Couldn't load platform data");
      setTenants([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSuspend = async (t) => {
    setBusyId(t.id);
    try {
      await api.put(`/platform/tenants/${t.id}/${t.platform_suspended ? "reactivate" : "suspend"}`);
      toast.success(t.platform_suspended ? `${t.name} reactivated` : `${t.name} suspended`);
      load();
    } catch { toast.error("Couldn't update workspace"); } finally { setBusyId(null); }
  };

  if (!tenants) {
    return <div className="py-16 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading platform data…</div>;
  }

  return (
    <div data-testid="platform-admin-page">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="h-10 w-10 rounded-xl aurelo-gradient flex items-center justify-center"><ShieldAlert className="h-5 w-5 text-white" /></div>
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Platform Admin</h1>
          <p className="text-sm text-slate-500">Cross-tenant visibility for 210 CodeLabs</p>
        </div>
      </div>
      {stats && (
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs text-slate-400">Workspaces</div><div className="text-2xl font-bold text-slate-900 mt-1" data-testid="platform-stat-tenants">{stats.total_tenants}</div></div>
          <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs text-slate-400">Total Users</div><div className="text-2xl font-bold text-slate-900 mt-1" data-testid="platform-stat-users">{stats.total_users}</div></div>
          <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="text-xs text-slate-400">Active Subscriptions</div><div className="text-2xl font-bold text-slate-900 mt-1" data-testid="platform-stat-active-subs">{stats.active_subscriptions}</div></div>
        </div>
      )}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70"><tr className="text-left text-xs font-semibold text-slate-600">
            <th className="px-4 py-3">Workspace</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Members</th>
            <th className="px-4 py-3 w-32"></th>
          </tr></thead>
          <tbody data-testid="platform-tenants-table-body">
            {tenants.map((t) => (
              <tr key={t.id} data-testid={`platform-tenant-row-${t.id}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-slate-400" />{t.name}</td>
                <td className="px-4 py-3">{t.plan || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="capitalize">{t.subscription_status_live || "—"}</Badge>
                  {t.platform_suspended && <Badge className="ml-1.5 bg-red-50 text-red-700 border-0">Suspended</Badge>}
                </td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" />{t.member_count}</span></td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`platform-toggle-suspend-${t.id}`} disabled={busyId === t.id} onClick={() => toggleSuspend(t)}>
                    {busyId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (t.platform_suspended ? "Reactivate" : "Suspend")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tenants.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No workspaces yet.</div>}
      </div>
    </div>
  );
}
